import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MATTRACKERAPIConstants } from '../constants/constants';
import { ApiResponse } from '../models';
import { SharedDataService } from './shared-data.service';

export interface User {
  user_id?: number;
  first_name: string;
  last_name: string;
  display_name: string;
  email_id: string;
  mobile_phone_number: string;
  user_role_cd: string;
  is_user_active: boolean;
  is_email_enabled: boolean;
  created_at?: Date;
  created_by?: string;
  updated_at?: Date;
  updated_by?: string;
}

export interface CreateUserRequest {
  first_name: string;
  last_name: string;
  display_name: string;
  email_id: string;
  mobile_phone_number?: string;
  user_role_cd: string;
  is_user_active: boolean;
  is_email_enabled: boolean;
}

export interface UpdateUserRequest {
  user_id: number;
  first_name: string;
  last_name: string;
  display_name: string;
  email_id: string;
  mobile_phone_number?: string;
  user_role_cd: string;
  is_user_active: boolean;
  is_email_enabled: boolean;
}

export interface CurrentUserInfoResponse {
  success: boolean;
  user?: {
    user_id: number;
    email: string;
    user_role_cd: string;
    display_name: string;
    is_active: boolean;
    is_email_enabled: boolean;
  };
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly baseUrl = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.GET_USERS}`;
  private readonly currentUserInfoUrl = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.GET_USER_INFO}`;

  private readonly _currentUserInfo$ = new BehaviorSubject<CurrentUserInfoResponse | null>(null);
  currentUserInfo$ = this._currentUserInfo$.asObservable();

  constructor(
    private http: HttpClient,
    private sharedData: SharedDataService
  ) {}

  /**
   * Get all users.
   * Cached in SharedDataService — call clearUsersCache() after add/update.
   */
  getUsers(): Observable<any> {
    return this.sharedData.getUsers();
  }

  /**
   * Get user by ID
   */
  getUserById(userId: number): Observable<any> {
    const url = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.GET_USER_BY_ID}/${userId}`;
    return this.http.get<any>(url)
      .pipe(catchError(this.handleError));
  }

  /**
   * Create new user and invalidate the users cache.
   */
  createUser(user: CreateUserRequest): Observable<any> {
    const url = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.ADD_USER}`;
    return this.http.post<any>(url, user).pipe(
      tap(() => this.sharedData.clearUsersCache()),
      catchError(this.handleError)
    );
  }

  /**
   * Update existing user and invalidate the users cache.
   */
  updateUser(user: UpdateUserRequest): Observable<any> {
    const url = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.UPDATE_USER}`;
    return this.http.put<any>(url, user).pipe(
      tap(() => this.sharedData.clearUsersCache()),
      catchError(this.handleError)
    );
  }

  /**
   * Toggle user active status
   */
  toggleUserStatus(userId: number, isActive: boolean): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.baseUrl}/${userId}/status`, { is_user_active: isActive })
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete user (soft delete - set is_user_active to false) and invalidate cache.
   */
  deleteUser(userId: number): Observable<any> {
    const url = `${environment.apiBaseUrl}/${MATTRACKERAPIConstants.DELETE_USER}`;
    return this.http.delete<any>(url, { body: { user_id: userId } }).pipe(
      tap(() => this.sharedData.clearUsersCache()),
      catchError(this.handleError)
    );
  }

  /**
   * Get current logged-in user's info (role, active flag, etc.)
   */
  getUserInfo(): Observable<CurrentUserInfoResponse> {
    return this.http.get<CurrentUserInfoResponse>(this.currentUserInfoUrl)
      .pipe(catchError(this.handleError));
  }

  setCurrentUserInfo(info: CurrentUserInfoResponse | null): void {
    this._currentUserInfo$.next(info);
  }

  private handleError(error: any): Observable<never> {
    console.error('User Service Error:', error);
    return throwError(() => new Error(error?.error?.message || 'An error occurred'));
  }
}
